import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbHighlight } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, Subject, catchError, finalize, takeUntil, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderStatus } from 'src/app/core/models/sales/company-sales-order-status.type';
import { CompanySalesOrderUpdate } from 'src/app/core/models/sales/company-sales-order-update.interface';
import {
  SalesOrderInsufficientStockLine,
  SalesOrderInsufficientStockResponse,
} from 'src/app/core/models/sales/sales-order-insufficient-stock.interface';
import { SalesOrderClientOption } from 'src/app/core/models/sales/sales-order-client-option.interface';
import { SalesOrderHeaderFormModel } from 'src/app/core/models/sales/sales-order-header-form.model';
import { SalesOrderService } from 'src/app/core/services/sales-order.service';
import {
  clientOptionCompare,
  clientOptionFilter,
  clientOptionFormatter,
} from '../../sales-order-client-search.util';
import { SalesOrderIntegrationView } from '../sales-order-overview.models';

export interface SalesOrderOverviewCustomerView {
  title: string;
  typeLabelKey: string;
  responsibleUserName: string;
  isOrganization: boolean;
  isContact: boolean;
}

@Component({
  selector: 'app-sales-order-overview-tab',
  standalone: true,
  imports: [CommonModule, CoreModule, FormsModule, NgbHighlight, ReactiveFormsModule, TranslateModule],
  providers: [SalesOrderHeaderFormModel],
  templateUrl: './sales-order-overview-tab.component.html',
  styleUrl: './sales-order-overview-tab.component.scss',
})
export class SalesOrderOverviewTabComponent implements OnChanges, OnDestroy {
  readonly headerFormModel = inject(SalesOrderHeaderFormModel);
  private readonly salesOrderService = inject(SalesOrderService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public salesOrderId = '';
  @Input({ required: true }) public salesOrder: CompanySalesOrderDetails | null = null;
  @Input({ required: true }) public clientOptions: SalesOrderClientOption[] = [];
  @Input({ required: true }) public statusOptions: { value: CompanySalesOrderStatus; labelKey: string }[] = [];
  @Input() public integrationView: SalesOrderIntegrationView | null = null;

  @Output() public readonly orderUpdated = new EventEmitter<CompanySalesOrderDetails>();
  @Output() public readonly insufficientStock = new EventEmitter<SalesOrderInsufficientStockLine[]>();
  // Quick-action outputs — the parent overview routes the user to the correct tab and triggers
  // the existing create-invoice / create-shipment modal there. Duplicate is owned by the parent.
  @Output() public readonly issueInvoiceRequested = new EventEmitter<void>();
  @Output() public readonly createShipmentRequested = new EventEmitter<void>();
  @Output() public readonly duplicateOrderRequested = new EventEmitter<void>();

  // Mirrors the `isDuplicating` flag from the parent so we can disable the duplicate button
  // while the request is in flight. The parent binds this input.
  @Input() public isDuplicating = false;

  public isSavingHeader = false;
  public headerErrorKey = '';
  public isStatusConfirmationOpen = false;
  // Modal-driven transitions: confirm (New -> Confirmed), revert (Confirmed -> New),
  // and cancel (* -> Cancelled). Draft <-> New skips the modal.
  public statusConfirmationVariant: 'confirm' | 'cancel' | 'revert' | null = null;
  public statusConfirmationTitleKey = '';
  public statusConfirmationPromptKey = '';
  public statusConfirmationActionKey = '';
  public statusConfirmationLoadingKey = '';
  public statusConfirmationActionClass = '';

  public customerView: SalesOrderOverviewCustomerView | null = null;
  public statusSelectItems: { key: string; value: CompanySalesOrderStatus }[] = [];
  public statusLabelKey = '';
  // Precomputed gate for the invoice + shipment quick actions so the template never
  // invokes a method/getter for visual state (AGENTS.md template rule). Cancelled is
  // terminal and archived orders lock these actions to match the rest of the editing surface.
  public isQuickActionLocked = true;

  public readonly clientFilterFn = clientOptionFilter;
  public readonly clientFormatter = clientOptionFormatter;
  public readonly clientCompareFn = clientOptionCompare;

  private pendingPayload: CompanySalesOrderUpdate | null = null;
  private readonly unsubscribe$ = new Subject<void>();

  public get headerForm() {
    return this.headerFormModel.form;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['salesOrder'] || changes['clientOptions']) {
      this.customerView = this.buildCustomerView();
      // Defer to a microtask so the form value lands after the parent's
      // value-accessors have initialized; preserves prior behaviour for tests
      // that drain a microtask before reading the form.
      queueMicrotask(() => this.syncFormFromOrder());
    }
    if (changes['statusOptions']) {
      this.statusSelectItems = this.statusOptions.map((option) => ({
        key: option.labelKey,
        value: option.value,
      }));
    }
    if (changes['salesOrder'] || changes['statusOptions']) {
      this.statusLabelKey = this.resolveStatusLabelKey();
    }
    if (changes['salesOrder']) {
      const order = this.salesOrder;
      this.isQuickActionLocked = !order || order.status === 'Cancelled' || !!order.isArchived;
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onStatusFieldUpdated(): void {
    this.saveHeader();
  }

  public onRequestedDeliveryDateFieldUpdated(): void {
    this.saveHeader();
  }

  public onShortDescriptionFieldUpdated(): void {
    this.saveHeader();
  }

  public onCustomerFieldUpdated(): void {
    this.saveHeader();
  }

  public onIssueInvoice(): void {
    if (this.isQuickActionLocked) {
      return;
    }
    this.issueInvoiceRequested.emit();
  }

  public onCreateShipment(): void {
    if (this.isQuickActionLocked) {
      return;
    }
    this.createShipmentRequested.emit();
  }

  public onDuplicateOrder(): void {
    // Duplicate is allowed from every state (incl. Cancelled / archived) — the backend
    // does not enforce edit-lock on the source for duplication. We only gate concurrent
    // in-flight clicks so a double-click cannot fire two POSTs.
    if (this.isDuplicating) {
      return;
    }
    this.duplicateOrderRequested.emit();
  }

  public saveHeader(): void {
    if (!this.companyId || !this.salesOrderId || this.isSavingHeader) {
      return;
    }

    const selectedClient = this.findClientOptionById(this.headerForm.controls.clientId.value);
    const requestedStatus = this.headerForm.controls.status.value;

    const payload: CompanySalesOrderUpdate = {
      customerNumber: selectedClient ? selectedClient.customerNumber : null,
      // Backend resolves these to Customer.Id and persists SalesOrder.CustomerId so
      // the customer link survives reloads (independent of customerNumber matching).
      clientOrganizationId:
        selectedClient?.type === 'organization' ? selectedClient.entityId : null,
      clientContactId:
        selectedClient?.type === 'contact' ? selectedClient.entityId : null,
      status: requestedStatus,
      requestedDeliveryDate: this.headerForm.controls.requestedDeliveryDate.value,
      // Backend treats null as "unchanged" and empty string as "clear", so always
      // send a string — '' when the user cleared the field.
      shortDescription: this.headerForm.controls.shortDescription.value ?? '',
    };

    // High-impact transitions (confirm reserves stock, cancel is terminal, revert releases
    // reservations) are gated by an explicit user confirmation prompt to prevent
    // accidental clicks. Lightweight transitions (Draft <-> New) submit immediately so
    // the number allocation is the user-visible signal.
    const variant = this.confirmationVariantFor(requestedStatus);
    if (variant) {
      this.openStatusConfirmation(variant, payload);
      return;
    }

    this.submitPayload(payload);
  }

  public confirmStatusChange(): void {
    if (this.isSavingHeader || !this.pendingPayload) {
      return;
    }
    const payload = this.pendingPayload;
    this.pendingPayload = null;
    this.isStatusConfirmationOpen = false;
    this.submitPayload(payload);
  }

  public closeStatusConfirmation(): void {
    if (this.isSavingHeader) {
      return;
    }
    this.pendingPayload = null;
    this.isStatusConfirmationOpen = false;
    this.statusConfirmationVariant = null;
  }

  private buildCustomerView(): SalesOrderOverviewCustomerView | null {
    const order = this.salesOrder;
    if (!order) {
      return null;
    }

    const match = this.findClientOptionForOrder(order);
    if (!match) {
      return null;
    }

    return {
      title: match.title,
      typeLabelKey: match.typeLabelKey,
      responsibleUserName: match.responsibleUserName ?? '',
      isOrganization: match.type === 'organization',
      isContact: match.type === 'contact',
    };
  }

  private findClientOptionForOrder(order: CompanySalesOrderDetails): SalesOrderClientOption | null {
    if (order.customerOrganizationId) {
      const matchByOrg = this.clientOptions.find(
        (option) => option.type === 'organization' && option.entityId === order.customerOrganizationId,
      );
      if (matchByOrg) {
        return matchByOrg;
      }
    }

    if (order.customerContactId) {
      const matchByContact = this.clientOptions.find(
        (option) => option.type === 'contact' && option.entityId === order.customerContactId,
      );
      if (matchByContact) {
        return matchByContact;
      }
    }

    return null;
  }

  private resolveStatusLabelKey(): string {
    const status = this.salesOrder?.status ?? null;
    if (!status) {
      return '';
    }
    const match = this.statusOptions.find((option) => option.value === status);
    return match ? match.labelKey : '';
  }

  private openStatusConfirmation(
    variant: 'confirm' | 'cancel' | 'revert',
    payload: CompanySalesOrderUpdate,
  ): void {
    this.pendingPayload = payload;
    this.headerErrorKey = '';
    this.statusConfirmationVariant = variant;
    if (variant === 'confirm') {
      this.statusConfirmationTitleKey = 'COMPONENTS.SALES_ORDERS.MODAL.CONFIRM_TITLE';
      this.statusConfirmationPromptKey = 'COMPONENTS.SALES_ORDERS.MODAL.CONFIRM_PROMPT';
      this.statusConfirmationActionKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.CONFIRM_ORDER';
      this.statusConfirmationLoadingKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.CONFIRMING';
      this.statusConfirmationActionClass = 'sales-order-confirmation__primary-button';
    } else if (variant === 'revert') {
      this.statusConfirmationTitleKey = 'COMPONENTS.SALES_ORDERS.MODAL.REVERT_TO_NEW_TITLE';
      this.statusConfirmationPromptKey = 'COMPONENTS.SALES_ORDERS.MODAL.REVERT_TO_NEW_PROMPT';
      this.statusConfirmationActionKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.REVERT_TO_NEW';
      this.statusConfirmationLoadingKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.REVERTING';
      this.statusConfirmationActionClass = 'sales-order-confirmation__primary-button';
    } else {
      this.statusConfirmationTitleKey = 'COMPONENTS.SALES_ORDERS.MODAL.CANCEL_TITLE';
      this.statusConfirmationPromptKey = 'COMPONENTS.SALES_ORDERS.MODAL.CANCEL_PROMPT';
      this.statusConfirmationActionKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.CANCEL_ORDER';
      this.statusConfirmationLoadingKey = 'COMPONENTS.SALES_ORDERS.BUTTONS.CANCELLING';
      this.statusConfirmationActionClass = 'sales-order-confirmation__danger-button';
    }
    this.isStatusConfirmationOpen = true;
  }

  private confirmationVariantFor(
    requestedStatus: CompanySalesOrderStatus | null,
  ): 'confirm' | 'cancel' | 'revert' | null {
    const currentStatus = this.salesOrder?.status ?? null;
    if (requestedStatus === currentStatus) {
      return null;
    }
    if (requestedStatus === 'Cancelled') {
      return 'cancel';
    }
    // Confirmed -> New releases reservations on the backend and is blocked when shipments are
    // linked. Surface a confirmation prompt so the user knows the side effects.
    if (requestedStatus === 'New' && currentStatus === 'Confirmed') {
      return 'revert';
    }
    // New -> Confirmed reserves stock; existing confirm modal copy applies.
    if (requestedStatus === 'Confirmed' && currentStatus === 'New') {
      return 'confirm';
    }
    // Draft <-> New is a lightweight transition (number allocation/preservation only);
    // submit immediately without a modal.
    return null;
  }

  private submitPayload(payload: CompanySalesOrderUpdate): void {
    this.isSavingHeader = true;
    this.headerErrorKey = '';

    this.salesOrderService
      .updateSalesOrder(this.companyId, this.salesOrderId, payload)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order) => this.orderUpdated.emit(order)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 422 && this.isInsufficientStockResponse(error.error)) {
            this.headerErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.INSUFFICIENT_STOCK';
            this.insufficientStock.emit(error.error.lines);
          } else if (error.status === 400) {
            const code = (error.error as { code?: string } | null)?.code;
            this.headerErrorKey = this.mapStatusErrorCode(code);
          } else {
            this.headerErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.SAVE_FAILED';
          }
          return EMPTY;
        }),
        finalize(() => {
          this.isSavingHeader = false;
        }),
      )
      .subscribe();
  }

  private mapStatusErrorCode(code: string | undefined): string {
    if (code === 'ORDER_HAS_SHIPMENTS') {
      return 'COMPONENTS.SALES_ORDERS.ERRORS.ORDER_HAS_SHIPMENTS';
    }
    if (code === 'ORDER_TERMINAL') {
      return 'COMPONENTS.SALES_ORDERS.ERRORS.ORDER_TERMINAL';
    }
    return 'COMPONENTS.SALES_ORDERS.ERRORS.STATUS_TRANSITION_INVALID';
  }

  private isInsufficientStockResponse(payload: unknown): payload is SalesOrderInsufficientStockResponse {
    return (
      typeof payload === 'object' &&
      payload !== null &&
      (payload as { code?: unknown }).code === 'INSUFFICIENT_STOCK' &&
      Array.isArray((payload as { lines?: unknown }).lines)
    );
  }

  private syncFormFromOrder(): void {
    const order = this.salesOrder;
    if (!order) {
      return;
    }

    const clientId = this.findClientId(order);

    this.headerForm.reset({
      clientId,
      status: order.status,
      requestedDeliveryDate: this.toDateInputValue(order.requestedDeliveryDate),
      shortDescription: order.shortDescription ?? null,
    });
  }

  private findClientId(order: CompanySalesOrderDetails): string | null {
    // Prefer the persisted Customer link (Customer.OrganizationId / Customer.ContactId)
    // because it's a stable Guid mapping that survives customerNumber reformatting,
    // BC sync, and renames. Fall back to the legacy customerNumber match for orders
    // created before the link was persisted.
    if (order.customerOrganizationId) {
      const matchByOrg = this.clientOptions.find(
        (option) => option.type === 'organization' && option.entityId === order.customerOrganizationId,
      );
      if (matchByOrg) {
        return matchByOrg.id;
      }
    }

    if (order.customerContactId) {
      const matchByContact = this.clientOptions.find(
        (option) => option.type === 'contact' && option.entityId === order.customerContactId,
      );
      if (matchByContact) {
        return matchByContact.id;
      }
    }

    return this.findClientIdByCustomerNumber(order.customerNumber);
  }

  private toDateInputValue(value: string | Date | null | undefined): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      // <input type="date"> only accepts the yyyy-MM-dd portion; the API returns full ISO datetimes.
      return value.length >= 10 ? value.substring(0, 10) : value;
    }
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private findClientOptionById(clientId: string | null): SalesOrderClientOption | null {
    if (!clientId) {
      return null;
    }
    return this.clientOptions.find((option) => option.id === clientId) ?? null;
  }

  private findClientIdByCustomerNumber(customerNumber: string | null | undefined): string | null {
    const trimmed = customerNumber?.trim();
    if (!trimmed) {
      return null;
    }

    const match = this.clientOptions.find(
      (option) => option.customerNumber?.toLowerCase() === trimmed.toLowerCase(),
    );
    return match ? match.id : null;
  }
}
